import * as React from "react";
import { CheckIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import { Column } from "@tanstack/react-table";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

interface DataTableFacetedFilterProps<TData, TValue> {
  title: string;
  options?: {
    // Make options optional
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  selectedValues: Set<string>;
  onFilter: (values: Set<string>) => void;
  variant?: "dashed" | "regular"; // Existing prop
  fullWidth?: boolean; // Add this line
}

export function DataTableFacetedFilter<TData, TValue>({
  title,
  options = [], // Default to empty array
  onFilter,
  selectedValues,
  variant = "dashed",
  fullWidth = false, // Add default value
}: DataTableFacetedFilterProps<TData, TValue>) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`!h-auto min-h-[2rem] py-1.5 ${
            variant === "regular" ? "border" : "border-dashed"
          } ${fullWidth ? "w-full justify-start" : ""}`}
        >
          <PlusCircledIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="shrink-0">{title}</span>
          {selectedValues.size > 0 && (
            <>
              {title && (
                <Separator
                  orientation="vertical"
                  className="mx-2 h-4 shrink-0"
                />
              )}
              <Badge
                variant="default"
                className="rounded-sm px-1 font-normal lg:hidden shrink-0"
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden lg:flex flex-wrap gap-1 items-center">
                {selectedValues.size > 4 ? (
                  <Badge
                    variant="default"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="default"
                        key={option.value}
                        className="rounded-sm px-1 font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={`w-[200px] p-0 ${fullWidth ? "w-full" : ""}`}
        align="start"
      >
        <Command className={fullWidth ? "w-full" : ""}>
          <CommandInput
            placeholder={`Search ${title}`}
            className={fullWidth ? "w-full" : ""}
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const newSelected = new Set(selectedValues);
                      if (isSelected) {
                        newSelected.delete(option.value);
                      } else {
                        newSelected.add(option.value);
                      }
                      onFilter(newSelected);
                    }}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>
                    {option.icon && (
                      <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    )}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onFilter(new Set())}
                    className="justify-center text-center"
                  >
                    Clear
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
